/* eslint-disable max-statements */
import { Button, Group, Textarea, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';

import { Declaration, DeclarationCustomsType } from '@e-origin/shared';

import { MultiLangCodes, TextInput } from '@components';
import { useGenerateBatchByLrn } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { canBeAmended } from '../helpers';

interface DeclarationDuplicateAndAmendmentProps {
  declaration: Declaration;
  isRfiAmendment?: boolean;
}

interface DuplicationAndAmendmentFormValues {
  batchName: string;
  amendmentMotivation: string;
  amendmentReason: string;
}

export const DeclarationDuplicationAndAmendment: React.FC<DeclarationDuplicateAndAmendmentProps> = ({ declaration, isRfiAmendment = false }) => {
  const isPLDA = declaration.customsState?.sentTo === DeclarationCustomsType.PLDA;
  const isFrLowValue = declaration.customsState?.sentTo === DeclarationCustomsType.FR_DELTA_IE_LV;
  const isFrDeltaIe = declaration.customsState?.sentTo === DeclarationCustomsType.FR_DELTA_IE_HV || isFrLowValue;
  const showAmendmentButton = canBeAmended(declaration) || isRfiAmendment;
  const regularizationText = isPLDA ? 'Regularization' : 'Amendment';
  const batchNamePrefix = showAmendmentButton ? (isPLDA ? 'REGUL' : 'AMEND') : 'DUPL';

  const codelistSystems = useCodeListSystem({ group: declaration.generalInfo?.group, isExport: declaration.generalInfo?.isExport });
  const generateBatchByLRNMutation = useGenerateBatchByLrn();

  const form = useForm<DuplicationAndAmendmentFormValues>({
    initialValues: {
      batchName: batchNamePrefix + (declaration.consignment?.referenceNumberUCR ?? declaration.goodsShipmentItems?.[0]?.referenceNumberUCR ?? ''),
      amendmentMotivation: '',
      amendmentReason: '',
    },
    validate: {
      amendmentMotivation: (value) => (isFrDeltaIe && showAmendmentButton && !value.trim() ? 'Amendment motivation code is required' : null),
    },
  });

  const handleAmendment = async (values: DuplicationAndAmendmentFormValues) => {
    await generateBatchByLRNMutation.mutateAsync({
      lrn: declaration.messageInfo.LRN,
      batchName: values.batchName,
      regulate: true,
      ...(isFrDeltaIe ? { amendmentMotivation: values.amendmentMotivation, amendmentReason: values.amendmentReason || undefined } : {}),
    });
    modals.closeAll();
  };

  const handleDuplicate = async (values: DuplicationAndAmendmentFormValues) => {
    await generateBatchByLRNMutation.mutateAsync({ lrn: declaration.messageInfo.LRN, batchName: values.batchName, regulate: false });
    modals.closeAll();
  };

  return (
    <form onSubmit={form.onSubmit(showAmendmentButton ? handleAmendment : handleDuplicate)}>
      <TextInput label="Batch name" {...form.getInputProps('batchName')} />
      {isFrDeltaIe && showAmendmentButton && (
        <>
          <MultiLangCodes
            label="Amendment motivation code"
            code="RECORD_NIS_MOTIFS_RECTIFICATION"
            system={codelistSystems}
            {...form.getInputProps('amendmentMotivation')}
            required
            mt="sm"
          />
          <Textarea label="Amendment reason" maxLength={isFrLowValue ? 350 : 512} mt="sm" {...form.getInputProps('amendmentReason')} />
        </>
      )}
      <Group justify="center" mt="md">
        {!isRfiAmendment && !isFrLowValue && (
          <Tooltip label={isPLDA ? 'Duplicate and Invalidate' : 'Duplicate'}>
            <Button type="button" onClick={() => handleDuplicate(form.values)} variant="filled" size="lg" loading={generateBatchByLRNMutation.isPending}>
              Duplicate
            </Button>
          </Tooltip>
        )}
        {showAmendmentButton && (
          <Button type="submit" loading={generateBatchByLRNMutation.isPending} variant="filled" size="lg">
            {regularizationText}
          </Button>
        )}
      </Group>
    </form>
  );
};
